import { safeMap, SafePromise } from '@benzinga/safe-await';
import { UserSubscription, SubscriptionStatus, Coupon } from './entities';

export interface IngressCard {
  brand: string;
  country: string;
  exp_month: number;
  exp_year: number;
  last_4: string;
  name: string;
  uuid: string;
  zip_code: string;
}

export interface IngressSubscription {
  amount: number;
  base_plan: string;
  cancel_at_period_end: boolean;
  canceled_on?: string;
  coupon: IngressCoupon | null;
  currency: string;
  current_period_end: string;
  date_created: string;
  final_price: string;
  interval: 'year';
  interval_count: number;
  is_paying_subscription: boolean;
  line_items: {
    amount: string;
    currency: string;
    date_created: string;
    feature_name: string;
    feature_slug: string;
    localized_price: string;
    uuid: string;
  }[];
  localized_price: string;
  plan_name: string;
  product: string;
  product_name: string;
  status: string;
  total_price: string;
  trial_end: string | null;
  uuid: string;
  will_activate_on: string;
  reactivate: boolean;
  parent: string | undefined;
}

export declare type CouponDuration = 'once' | 'repeating' | 'forever';
export interface IngressCoupon {
  amount_off: number | null;
  code: string | null;
  currency: string | null;
  duration: CouponDuration;
  duration_in_months: number | null;
  is_valid: boolean;
  name: string;
  percent_off: number | null;
  plans: string[] | null;
  redeem_by: string | null;
}

export const ingressCoupon = (ingress: IngressCoupon): Coupon => ({
  amountOff: ingress.amount_off,
  code: ingress.code,
  currency: ingress.currency,
  duration: ingress.duration,
  durationInMonths: ingress.duration_in_months,
  isValid: ingress.is_valid,
  name: ingress.name,
  percentOff: ingress.percent_off,
  plans: ingress.plans ?? null,
});

export const ingestSubscriptions = (subscriptions: IngressSubscription[]): UserSubscription[] =>
  subscriptions?.map(ingestSubscription);

export const ingestSubscription = (subscriptions: IngressSubscription): UserSubscription => ({
  amount: subscriptions.amount,
  basePlan: subscriptions.base_plan,
  cancelAtPeriodEnd: subscriptions.cancel_at_period_end,
  canceledOn: subscriptions?.canceled_on || '',
  coupon: subscriptions.coupon ? ingressCoupon(subscriptions.coupon) : null,
  currency: subscriptions.currency,
  currentPeriodEnd: subscriptions.current_period_end,
  dateCreated: subscriptions.date_created,
  finalPrice: subscriptions.final_price,
  interval: subscriptions.interval,
  intervalCount: subscriptions.interval_count,
  isPayingSubscription: subscriptions.is_paying_subscription,
  lineItems: subscriptions.line_items.map(l => ({
    amount: l.amount,
    currency: l.currency,
    dateCreated: l.date_created,
    featureName: l.feature_name,
    featureSlug: l.feature_slug,
    localizedPrice: l.localized_price,
    uuid: l.uuid,
  })),
  localizedPrice: subscriptions.localized_price,
  parent: subscriptions.parent,
  planName: subscriptions.plan_name,
  product: subscriptions.product,
  productName: subscriptions.product_name,
  reactivate: subscriptions.reactivate,
  status: subscriptions.status as SubscriptionStatus,
  totalPrice: subscriptions.total_price,
  trialEnd: subscriptions.trial_end,
  uuid: subscriptions.uuid,
  willActivateOn: subscriptions.will_activate_on,
});

export interface IngressSmsResponse {
  sms_verified?: boolean;
}

export const ingestSmsResponse = async (incomingSmsResponse: SafePromise<IngressSmsResponse>): SafePromise<boolean> =>
  safeMap(incomingSmsResponse, sms => sms.sms_verified ?? false);

export interface IngressHumanResponse {
  human_verified?: boolean;
}

export const ingestHumanResponse = async (
  incomingHumanResponse: SafePromise<IngressHumanResponse>,
): SafePromise<boolean> => safeMap(incomingHumanResponse, sms => sms.human_verified ?? false);
